import { Button } from '@/components/ui/button';
import { ArrowLeft, Construction } from 'lucide-react';
import { Link, useNavigate } from '@tanstack/react-router';

interface ComingSoonProps {
  title?: string;
  description?: string;
  showBackButton?: boolean;
}

/**
 * ComingSoon component displays a placeholder page for features that are not yet implemented
 * @param title - The title to display (defaults to "Coming Soon")
 * @param description - The description to display (defaults to "This feature is under construction")
 * @param showBackButton - Whether to show a back button (defaults to true)
 */
function ComingSoon({ 
  title = "Coming Soon", 
  description = "This feature is under construction and will be available soon.",
  showBackButton = true 
}: ComingSoonProps) {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate({ to: '/' });
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-surface-light-sl-1 dark:bg-surface-dark-sl-1">
      <div className="flex flex-col items-center justify-center space-y-6 text-center">
        {/* Construction Icon */}
        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-muted">
          <Construction className="h-12 w-12 text-muted-foreground" />
        </div>

        {/* Title */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            {title}
          </h1>
          <p className="text-lg text-muted-foreground max-w-md">
            {description}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-4">
          {showBackButton && (
            <Button
              variant="outline"
              onClick={handleGoBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Go Back
            </Button>
          )}
          <Button asChild>
            <Link to="/">
              Go to Home
            </Link>
          </Button>
        </div>

        {/* Additional Info */}
        <div className="mt-8 rounded-lg border bg-surface-light-sl-1 dark:bg-surface-dark-sl-1 p-4 text-sm text-muted-foreground">
          <p>We're working hard to bring you this feature. Stay tuned for updates!</p>
        </div>
      </div>
    </div>
  );
}

export default ComingSoon; 
